import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Select } from '@ngxs/store';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { UserHttpService } from '@services/datasources/user-http.service';
import { OrganizationState } from '@state/organization.state';
import { userRoles } from 'app/@types/dto/userRoles.model';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { ConfirmationService } from 'primeng/api';
import { Observable, Subject, combineLatest, map } from 'rxjs';
import { UserInfoDto } from 'app/@types/dto/user-info-dto.model';
import { SecurityRoleDto } from 'app/@types/dto/security-role-dto.model';
import { RoleHttpService } from '@services/datasources/role-http.service';
import { UserState } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';

@Component({
    selector: 'app-edit-user',
    templateUrl: './edit-user.component.html',
    styleUrls: ['./edit-user.component.scss'],
    providers: [ConfirmationService],
})
export class EditUserComponent implements OnInit {
    @Output() closeEditUser = new EventEmitter<{ translateKey: string; type: string } | undefined>();
    @Select(UserState.getCurrentUserClaims) userClaim$: Observable<UserClaims>;

    @Select(OrganizationState.getEntities) entities$: Observable<OrganizationEntityDto[]>;
    entitiesOptions: OrganizationEntityDto[] = [];
    userForm: FormGroup;
    securityProfileOption = [
        { id: 'c1eee594-6453-4b95-bd80-541348fc2f6b', value: userRoles.main_user, label: 'Owner' },
        { id: '43f2cdf8-8a25-4bc9-94cd-6314958f06ad', value: userRoles.secondary_user, label: 'Guest' },
    ];
    @Input() user: UserInfoDto | null = null;
    @Input() role: string | null;
    @Input() orgId: string | null;
    @Input() isOrgOwner: boolean = false;
    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly translateService: CustomTranslateService,
        private readonly confirmationService: ConfirmationService,
        private readonly userService: UserHttpService,
        private readonly roleService: RoleHttpService,
    ) {
        this.userForm = this.formBuilder.group({
            lastname: this.formBuilder.control('', { validators: [Validators.required] }),
            firstname: this.formBuilder.control('', { validators: [Validators.required] }),
            email: this.formBuilder.control('', { validators: [Validators.required, CustomValidators.emailValidator] }),
            phone_number: this.formBuilder.control(''),
            role: this.formBuilder.control(''),
            entities: new FormControl<OrganizationEntityDto[] | null>(null, { validators: [Validators.required] }),
            profile: this.formBuilder.control(''),
        });
    }

    ngOnInit(): void {
        combineLatest([this.entities$, this.userClaim$]).subscribe(([entities, userClaim]) => {
            this.entitiesOptions = entities.filter((entity) => !entity.deleted);
            this.initValue();
            if (this.user?.uid) {
                this.userForm.disable();
                if (this.isOrgOwner) this.userForm.get('entities')?.enable();
            }
        });
    }

    close() {
        this.closeEditUser.emit();
    }

    initValue() {
        if (this.user) {
            this.userForm.patchValue(this.user);
            const role = this.user.roles.find((elt) => elt.organization_uid === this.orgId);
            if (role) {
                this.userForm.patchValue({ profile: this.securityProfileOption.find((it) => it.id === role.role_uid) });
            }
            if (this.user.roles.length > 0) {
                const userEntities = this.user.roles.map((role) => role.org_entity_uid);
                const entities = this.entitiesOptions
                    .filter((entity) => entity.id !== undefined)
                    .filter((entity) => userEntities.filter((elt) => elt !== undefined).includes(entity.id!));

                this.userForm.patchValue({ entities });
            }
        }
    }

    onDelete(): void {
        this.confirmationService.confirm({
            message: this.translateService.instant('common.confirm_message'),
            header: this.translateService.instant('common.confirm_title'),
            acceptLabel: this.translateService.instant('common.confirm_button'),
            rejectLabel: this.translateService.instant('common.cancel_button'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                if (this.orgId && this.user?.uid)
                    this.userService.removeUser(this.orgId, this.user.uid).subscribe(
                        () =>
                            this.closeEditUser.emit({
                                translateKey: 'settings.users.toast.success.remove',
                                type: 'success',
                            }),
                        () =>
                            this.closeEditUser.emit({
                                translateKey: 'settings.users.toast.failed.remove',
                                type: 'success',
                            }),
                    );
            },
            reject: () => {
                this.closeEditUser.emit();
            },
        });
    }

    save() {
        const formValue = this.userForm.value;
        const userData: any = {
            email: formValue.email,
            phone_number: formValue.phone_number,
            locale: 'en',
            firstname: formValue.firstname,
            lastname: formValue.lastname,
            roles: formValue.entities.map((entity) => {
                return {
                    org_entity_uid: entity.id,
                    organization_uid: entity.organizationId,
                };
            }),
        };

        if (this.user?.uid) {
            const entities = this.userForm.get('entities')?.value;
            if (this.orgId && entities) {
                const data: SecurityRoleDto[] = entities.map((entity) => {
                    return {
                        role_uid: this.securityProfileOption[1].id,
                        role_name: this.securityProfileOption[1].value,
                        user_uid: this.user?.uid,
                        organization_uid: this.orgId,
                        org_entity_uid: entity.id,
                    };
                });
                this.roleService.updateEntity(this.orgId, data).subscribe(
                    () => {
                        this.closeEditUser.emit({
                            translateKey: 'settings.users.toast.success.update',
                            type: 'success',
                        });
                    },
                    (err) => {
                        this.closeEditUser.emit({ translateKey: 'settings.users.toast.failed.update', type: 'error' });
                    },
                );
            }
        } else {
            if (userData.phone_number === '') {
                userData.phone_number = '+3200000000000';
            }
            this.userService.addUserToScan(userData).subscribe(
                () => {
                    this.closeEditUser.emit({ translateKey: 'settings.users.toast.success.add', type: 'success' });
                },
                (err) => {
                    this.closeEditUser.emit({ translateKey: 'settings.users.toast.failed.add', type: 'error' });
                },
            );
        }
    }
}
